import {TreeNode} from 'components';
import {DynamicComponentNames} from 'constants/client';
import {Language} from 'constants/language';
import {DateFormat, TimeFormat} from 'enums';

import {InteractiveChatItem} from './interactive-chat.type';
import {InteractiveChatData} from './live-help.type';
import {PreSessionFlow} from './pre-session-flow.type';
import {TextChatContentType} from './text-chat-message.type';

export type DynamicContentComponent = {
    name: DynamicComponentNames;
    template: string;
    styles?: string;
};

export type WhiteLabelSettings = {
    enabled: boolean;
    components: DynamicContentComponent[];
}

export type LiveHelpMessageSettings = {
    contentType: TextChatContentType;
    content: string;
    data?: InteractiveChatData;
}

export type ChatGptSettings = {
    openAIProperties: {
        enabled: boolean;
        apiKey: string;
    },
    advancedSettings: {
        historyLimit?: number;
        chatCompletionParams: {
            model: string;
            temperature?: number;
            max_tokens?: number;
            top_p?: number;
            frequency_penalty?: number;
            presence_penalty?: number;
        }
    },
    promts: {
        knowledgeBase: string;
        botInstruction: string;
        suggestionInstruction: string;
    }
    actions: {
        callingClassificationInstruction: string;
    }
}

export type ClaudeSettings = {
    anthropicProperties: {
        enabled: boolean;
        apiKey: string;
    },
    advancedSettings: {
        historyLimit?: number;
        messagesParams: {
            model: string;
            max_tokens?: number;
        }
    },
    promts: {
        knowledgeBase: string;
        botInstruction: string;
    }
    actions: {
        callingClassificationInstruction: string;
    }
}

export type SipMessagesSettings = {
    welcome: string;
    callingAgent: string;
    featureDisabled: string;
    chatbotError: string;
    endSessionSuccess: string;
    sessionNotFound: string;
    afterHours: string;
    agentsUnavailable: string;
    unexpectedError: string;
}

export type SipVoiceAgentSettings = {
    enabled: boolean;
    provider: 'internal-live-agent';
}

export type SipTwilioSettings = {
    enabled: boolean;
    phoneNumber?: string;
    domainSid: string;
    applicationSid: string;
    token: string;
    redirect: {
        enabled: boolean;
        flowWebhookUrl: string;
    }
    virtualAgent?: {
        enabled: boolean;
    }
    voiceAgent?: SipVoiceAgentSettings;
}

export type WatsonxSettings = {
    enabled: boolean;
    serviceUrl: string;
    version: string;
    sttModel: string;
    environmentId: string;
    authentication: {
        apiKey: string;
    }
}

export type MastraSettings = {
    enabled: boolean;
    baseUrl: string;
    agentId: string;
    memory?: boolean;
    // Client text chat only: transcription language used when the participant has not picked one.
    language?: string;
    authentication: {
        apiKey: string;
    }
    promts?: {
        botInstruction?: string;
        knowledgeBase?: string;
    }
}

export type InternalLiveAgentSettings = {
    enabled: boolean;
    baseUrl: string;
    agentKey: string;
    agentId: string;
}

export type DeepgramSettings = {
    enabled: boolean;
    sttModel: string;
    language: string;
    authentication: {
        apiKey: string;
    };
}

export type WhatsappSettings = {
    enabled: boolean;
    phoneNumberId: string;
    authentication: {
        accessToken: string;
    };
}

export type SipSettings = {
    messages: SipMessagesSettings;
    twilio: SipTwilioSettings;
}

export type LiveHelpSettings = {
    widget: {
        welcome?: {
            messages: LiveHelpMessageSettings[]
        },
        avatarWelcome?: {
            messages: LiveHelpMessageSettings[]
        },
        deescalate?: {
            messages: LiveHelpMessageSettings[]
        },
        autoReply?: {
            agentsAvailable: {
                messages: LiveHelpMessageSettings[]
            },
            agentsUnavailable: {
                messages: LiveHelpMessageSettings[]
            },
        },
        interactiveChat?: TreeNode<InteractiveChatItem>[];
        preSessionFlow?: PreSessionFlow;
    }
}

export type ClientWhiteLabelSaveResultsType = {
    sanitizedComponents?: DynamicContentComponent[];
}

export type AccountSettingsFormFields = {
    id: string;
    name: string;
    timeZone: string;
    dateFormat: DateFormat;
    timeFormat: TimeFormat;
    language: Language;
    logo: string;
}

export type IpInfo = {
    id?: string;
    ip: string;
    createdBy: number;
    description: string;
    createdAt: Date;
}

export type DomainInfo = {
    id?: string;
    domain: string;
    createdBy: number;
    description: string;
    createdAt: Date;
}

export enum MFAStatus {
    Disabled = 'disabled',
    Optional = 'optional',
    Required = 'required'
}

export type SecuritySettingsFormFields = {
    passwordExpirationDays: number;
    passwordExpireSoonDays: number;
    liveHelpCspWhitelist: {
        enabled: boolean;
        list: DomainInfo[];
    }
    cspWhitelist: {
        enabled: boolean;
        token: string;
        list: DomainInfo[];
    }
    ipWhitelist: {
        enabled: boolean;
        list: IpInfo[];
    },
    mfa: {
        status: MFAStatus
    }
}